import { Controller } from "@hotwired/stimulus"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

const DESKTOP = "(min-width: 768px) and (prefers-reduced-motion: no-preference)"
const MOBILE = "(max-width: 767px) and (prefers-reduced-motion: no-preference)"

// Connects to data-controller="public--report-assembly"
//
// Section C: the six real 78745 pages assemble on the desk. The fanned spread
// is the CSS default, so no JavaScript, reduced motion and a Turbo cache
// restore all show the finished pages; the cover sits still and the five
// behind it arrive from below — transforms and opacity only. Desktop pins the
// section for 1.5 viewports and scrubs the arrival; a phone plays it once as
// the section comes into view. Turbo snapshots the page before controllers
// disconnect, so the pin-spacer is reverted on turbo:before-cache as well.
//
// The pinned element is the container inside the section, never the section
// itself: GSAP reparents whatever it pins into a pin-spacer, Stimulus reads
// that as the controller's element leaving and re-entering the document, and
// the disconnect (revert) / connect (pin again) pair repeats inside one
// mutation batch — a freeze with the main thread never yielding.
export default class extends Controller {
  static targets = ["page", "pinned"]

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.revert = () => this.assembly.revert()
    document.addEventListener("turbo:before-cache", this.revert)
    this.assembly = gsap.context(() => {
      gsap.matchMedia().add({ desktop: DESKTOP, mobile: MOBILE }, ({ conditions }) => {
        gsap.timeline({ scrollTrigger: conditions.desktop ? this.pinned : this.once })
          .from(this.pageTargets.slice(1), { yPercent: 40, opacity: 0, stagger: 0.18, duration: 1, ease: "expo.out" })
      })
    }, this.element)
    document.fonts.ready.then(() => ScrollTrigger.refresh())
  }

  disconnect() {
    if (!this.revert) return

    document.removeEventListener("turbo:before-cache", this.revert)
    this.revert()
  }

  get pinned() {
    return { trigger: this.element, start: "top top", end: "+=150%", pin: this.pinnedTarget, scrub: 0.5, anticipatePin: 1 }
  }

  get once() {
    return { trigger: this.element, start: "top 70%", toggleActions: "play none none none" }
  }
}
